import TradeCard from "features/trades/TradesCards/TradeCard";
import {
  LanguageNameSpaces,
  TradeData,
  TradeDataWithKey,
  TradeStatus,
} from "@shared/types";
import { useTradesFlatList } from "features/trades/common/commonHooks";
import FlatList from "common/Lists/FlatList";
import React, { useContext, useMemo, useState } from "react";
import TradesEmpty from "features/trades/TradesEmpty";
import { typography } from "@shared/common/Themes/typography";
import useCardsListSelector from "@shared/common/CardsList/useCardsListSelector.tsx";
import { TradeActionsContext } from "@shared/features/trades/common/contexts.tsx";
import { TradesPageContext } from "@shared/features/trades/common/TradesPage.context.tsx";
import { CardsSelectorContext } from "@shared/common/CardsList/CardsSelector.context";
import BulkActions from "common/Table/BulkActions.tsx";
import { NEW_LOOK_TRADE_ROW_COLUMNS } from "features/trades/common/constants";
import { openMessage } from "utils/misc.tsx";
import { DrawerContext } from "common/Drawers/Drawer.context.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import styled, { css, useTheme } from "styled-components";
import { NEW_LOOK_RADIUS } from "common/NewLook/tokens.ts";
import { newLookListHead } from "features/trades/NewLook/newLookListRows.ts";
import { Download } from "lucide-react";
import { PHONE_TRADES_EXPORT_ID } from "testIds";
import { NEW_LOOK_PHONE_CONTROL } from "common/NewLook/layout.ts";
import {
  PhoneControlButton,
  PhoneSectionHead,
  PhoneSortMenu,
} from "features/dashboard/PhoneDashboard/PhoneParts";
import PhoneTradeRow, {
  PhoneTradeRowSkeleton,
} from "features/trades/NewLook/PhoneTradeRow.tsx";
import useExpandedTrade from "features/trades/NewLook/useExpandedTrade.ts";

interface MobileTradesCardsProps {
  renderTopContainer?: () => React.JSX.Element;
  chartFetching?: boolean;
  shouldFetchSocketPnl?: boolean;
  /** New look phone page: one grouped card of rows (not in drawers). */
  phone?: boolean;
  onExport?: () => void;
  isExporting?: boolean;
}

const getProfit = (trade: TradeDataWithKey) => {
  const graph = trade.graph_data;
  return graph && "profit" in graph ? Number(graph.profit) || 0 : 0;
};

type SortKey = "newest" | "amount" | "pnl" | "symbol";

const MobileTradesCards = ({
  renderTopContainer,
  chartFetching,
  shouldFetchSocketPnl = true,
  phone,
  onExport,
  isExporting,
}: MobileTradesCardsProps) => {
  const [sortKey, setSortKey] = useState<SortKey>("newest");
  const { expandedTradeId, toggleExpanded: togglePhoneExpanded } =
    useExpandedTrade();
  const {
    flatListData,
    cardsSorters,
    toggleExpanded,
    isExpanded,
    waitingTradesCardsSorters,
    waitingTradesToggleExpanded,
    waitingTradesIsExpanded,
    isOpenTrades,
    isLoading,
    isFetching,
    searchValue,
    onRefresh,
    isEmpty,
    filteredActiveTrades,
  } = useTradesFlatList();

  const {
    selectedTradesIDs,
    setSelectedTradesIDs,
    getRowSelectable,
    isBulkMode,
    setIsBulkMode,
    setBulkCloseTradesModalOpen,
  } = useContext(TradeActionsContext) || {};
  const { bulkActionTrades } = useContext(TradesPageContext)!;
  const { isDrawer } = useContext(DrawerContext) || {};
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const { isNewLook } = useTheme();
  const isPhone = !!phone && isNewLook && !isDrawer;

  const { isAllRowsSelected, getCheckboxProps, onSelectAll, onSelectRow } =
    useCardsListSelector<TradeData, number>({
      selectedRows: selectedTradesIDs || [],
      setSelectedRows: setSelectedTradesIDs,
      getKey: (tradeData: TradeData) => tradeData.id,
      allRows: bulkActionTrades,
      filteredRows: filteredActiveTrades,
      getRowSelectable,
      openMessage,
    });

  const phoneData = useMemo(() => {
    if (!isPhone) return flatListData;
    const trades = flatListData.filter(
      (item): item is TradeDataWithKey => !("isHeader" in item)
    );
    const sorter: Record<
      SortKey,
      (a: TradeDataWithKey, b: TradeDataWithKey) => number
    > = {
      newest: (a, b) => b.timestamp - a.timestamp,
      amount: (a, b) => b.allocated_amount_usd - a.allocated_amount_usd,
      pnl: (a, b) => getProfit(b) - getProfit(a),
      symbol: (a, b) => a.symbol.localeCompare(b.symbol),
    };
    const waiting = trades
      .filter((trade) => trade.status_value === TradeStatus.IN_CANCEL)
      .sort(sorter[sortKey]);
    const active = trades
      .filter((trade) => trade.status_value !== TradeStatus.IN_CANCEL)
      .sort(sorter[sortKey]);
    const data: typeof flatListData = [];
    if (waiting.length)
      data.push({ isHeader: true, isWaitingTrades: true }, ...waiting);
    data.push({ isHeader: true, isWaitingTrades: false });
    if (isLoading && !active.length)
      data.push(
        ...([0, 1, 2].map((index) => ({
          skeleton: true,
          key: `skeleton-${index}`,
        })) as unknown as TradeDataWithKey[])
      );
    else data.push(...active);
    return data;
  }, [isPhone, flatListData, sortKey, isLoading]);

  const sortOptions: { key: SortKey; label: string }[] = [
    { key: "newest", label: t("phone.sort_newest") },
    { key: "amount", label: t("phone.sort_amount") },
    { key: "pnl", label: t("phone.sort_pnl") },
    { key: "symbol", label: t("phone.sort_symbol") },
  ];

  const getEmptyState = (isWaiting?: boolean) => {
    return isEmpty(isWaiting) ? (
      <TradesEmpty
        isOpenTrades={isOpenTrades}
        iconWidth={90}
        titleStyle={{ maxWidth: 300, fontSize: typography.fontSizes.M }}
        isSearch={!!searchValue?.length}
      />
    ) : undefined;
  };

  const renderContent = ({
    item,
    index,
  }: {
    item: TradeDataWithKey | { isHeader: boolean; isWaitingTrades?: boolean };
    index: number;
  }) => {
    if (isPhone) {
      if (item && "isHeader" in item) {
        const emptyState = getEmptyState(item.isWaitingTrades);
        return (
          <>
            <PhoneSectionHead
              title={t("table.title", {
                status: t(isOpenTrades ? "tabs.open" : "tabs.closed"),
              })}
              trailing={
                item.isWaitingTrades ? undefined : (
                  <HeaderActions>
                    {onExport && (
                      <ExportButton
                        type="button"
                        aria-label={t("actions.export")}
                        title={t("actions.export")}
                        disabled={isExporting}
                        onClick={onExport}
                        data-testid={PHONE_TRADES_EXPORT_ID}
                      >
                        <Download size={15} strokeWidth={2} />
                      </ExportButton>
                    )}
                    <PhoneSortMenu
                      options={sortOptions}
                      value={sortKey}
                      onChange={setSortKey}
                      label={t("phone.sort_label")}
                    />
                  </HeaderActions>
                )
              }
            />
            {emptyState}
          </>
        );
      }
      if ("skeleton" in (item as object)) return <PhoneTradeRowSkeleton />;
      return (
        <PhoneTradeRow
          tradeData={item}
          isOpenTrades={isOpenTrades}
          expanded={expandedTradeId === item.id}
          onToggleExpanded={togglePhoneExpanded}
          allowTradeClick={true}
          shouldFetchSocketPnl={shouldFetchSocketPnl}
          showBulkActions={!isDrawer}
          hideActions={item.status_value === TradeStatus.IN_CANCEL}
        />
      );
    }
    if (item && "isHeader" in item) {
      const sorters = item.isWaitingTrades
        ? waitingTradesCardsSorters
        : cardsSorters;
      if (isNewLook) {
        // The sorters are the list panel's column header; an empty list
        // closes the panel under them.
        const emptyState = getEmptyState(item.isWaitingTrades);
        return (
          <NewLookListHead $closed={!!emptyState}>
            {sorters}
            {emptyState}
          </NewLookListHead>
        );
      }
      return (
        <div style={{ marginTop: 8 }}>
          {sorters}
          {getEmptyState(item.isWaitingTrades)}
        </div>
      );
    }
    const next = flatListData[index + 1];
    const isLastRow = !next || "isHeader" in next;
    const isWaiting = item?.status_value === TradeStatus.IN_CANCEL;
    return (
      <TradeCard
        tradeData={item}
        isOpenTrades={isOpenTrades}
        toggleExpanded={
          isWaiting ? waitingTradesToggleExpanded : toggleExpanded
        }
        isExpanded={
          isWaiting ? waitingTradesIsExpanded(item?.key) : isExpanded(item?.key)
        }
        allowTradeClick={true}
        shouldFetchSocketPnl={shouldFetchSocketPnl}
        showBulkActions={!isDrawer}
        listRow={isNewLook ? (isLastRow ? "last" : "middle") : undefined}
      />
    );
  };

  const handleSetIsBulkMode = (tradeData?: TradeData) => {
    setIsBulkMode?.(true);
    if (tradeData) {
      onSelectRow(tradeData, true);
    }
  };

  const exitBulkMode = () => {
    setIsBulkMode?.(false);
    setSelectedTradesIDs?.([]);
  };

  const cardsContextValue = useMemo(() => {
    if (!setIsBulkMode) return;
    return {
      isEditMode: !!isBulkMode,
      setIsEditMode: handleSetIsBulkMode,
      getCheckboxProps,
    };
  }, [isBulkMode, getCheckboxProps]);

  const numSelectedTrades = selectedTradesIDs?.length || 0;

  const actionButtonsData = [
    {
      onClick: () => {
        if (numSelectedTrades) {
          setBulkCloseTradesModalOpen?.(true);
        } else {
          openMessage(t("actions.select_at_least_one_trade"), "warning");
        }
      },
      disabled: numSelectedTrades === 0,
      text: t("actions.close_trades"),
    },
  ];

  return (
    <CardsSelectorContext.Provider value={cardsContextValue}>
      <FlatList
        data={phoneData}
        loading={isPhone ? false : isLoading}
        renderContent={renderContent}
        renderTopContainer={renderTopContainer}
        gap={isNewLook ? 0 : 12}
        paddingHorizontal={isPhone ? 0 : undefined}
        onRefresh={onRefresh}
        refreshing={isFetching || chartFetching}
        hasActionButton={true}
        style={STYLE}
        wrapperStyle={isPhone ? PHONE_WRAPPER_STYLE : WRAPPER_STYLE}
      />
      {isBulkMode && (
        <BulkActions
          actionButtonsData={actionButtonsData}
          unselectAll={() => setSelectedTradesIDs?.([])}
          selectAll={onSelectAll}
          numSelected={numSelectedTrades}
          isAllSelected={isAllRowsSelected}
          onCancel={exitBulkMode}
        />
      )}
    </CardsSelectorContext.Provider>
  );
};

export default React.memo(MobileTradesCards);

const STYLE = { flex: 1 };

/** The top of the list panel: its edges, and the sorters as column header. */
const NewLookListHead = styled.div<{ $closed?: boolean }>`
  ${newLookListHead(NEW_LOOK_TRADE_ROW_COLUMNS)};
  ${(p) =>
    p.$closed &&
    css`
      border-radius: ${NEW_LOOK_RADIUS.panel}px;
      padding-bottom: 16px;
    `}
`;
const WRAPPER_STYLE = { padding: 16 };
const PHONE_WRAPPER_STYLE = { padding: "0 0 8px" };

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const ExportButton = styled(PhoneControlButton)`
  justify-content: center;
  width: ${NEW_LOOK_PHONE_CONTROL}px;
  padding: 0;
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;
